import { Injectable } from "@angular/core";
import { Item, ItemCategory, ItemDimension, ItemSeason, ItemStatus } from "src/services/item/item.interface";
import { AuthService } from "../auth/auth.service";
import { ApiService } from "../http/api.service";
import { fetchEveryPage } from "../http/pagination.utils";
import { apiPath } from "../../shared/api-path.const";
import {
  BackendArguments,
  ItemResponse,
  ItemsDataPage,
  ItemsForUserResponse,
  CreateItemRequest,
  UpdateItemRequest,
  PacklistResponse,
} from "../../shared/backend-api.types";

type StaffFieldsQuery = { expand?: ("location" | "notes")[] };
type ItemsQuery = NonNullable<BackendArguments["ItemController_getItems"]["parameters"]["query"]> & StaffFieldsQuery;
type ItemsForUserQuery = NonNullable<BackendArguments["ItemController_getItemsForUser"]["parameters"]["query"]> &
  StaffFieldsQuery;

export interface ItemsForUserFilter {
  category?: ItemCategory;
  statuses?: ItemStatus[];
}

function toItem(response: ItemResponse): Item {
  return {
    id: response.itemId,
    bay: response.bay,
    brand: response.brand,
    category: response.category,
    city: response.city as Item["city"],
    color: response.color,
    createdAt: response.createdAt,
    description: response.description,
    dimension:
      response.height != null
        ? {
            dimensionUnit: response.dimensionUnit as ItemDimension["dimensionUnit"],
            height: response.height,
            length: response.length,
            weight: response.weight,
            weightUnit: response.weightUnit as ItemDimension["weightUnit"],
            width: response.width,
          }
        : undefined,
    image: response.image && response.image.toLowerCase() !== "no image" ? response.image : undefined,
    name: response.name,
    notes: response.notes ? [response.notes] : [],
    number: response.number,
    numberId: response.numberId,
    photo: response.image && response.image.toLowerCase() !== "no image" ? response.image : undefined,
    rack: undefined,
    reclaimedAt: response.reclaimedAt ?? undefined,
    row: response.row,
    seasons: (response.seasons ?? []) as unknown as ItemSeason[],
    status: response.status as unknown as ItemStatus,
    userId: response.userId,
    warehouse: response.warehouse,
  } as Item;
}

function notesToRequest(notes: Item["notes"] | string): string | undefined {
  return Array.isArray(notes) ? notes.join(", ") : notes;
}

const staffOnlyFields: StaffFieldsQuery = { expand: ["location", "notes"] };

@Injectable({
  providedIn: "root",
})
export class ItemApiService {
  private readonly inFlightByUserId = new Map<string, Promise<Item[]>>();

  constructor(
    private readonly apiService: ApiService,
    private readonly authService: AuthService,
  ) {}

  public async getById(id: string): Promise<Item> {
    const response = await this.apiService.sendGetRequest<ItemResponse, StaffFieldsQuery>(
      `${apiPath.item}/${id}`,
      await this.staffFieldsForActiveUser(),
    );
    return toItem(response);
  }

  public async getByUserId(userId: string, filter: ItemsForUserFilter = {}): Promise<Item[]> {
    const cacheKey = `${userId}:${filter.statuses?.join(",") ?? "all"}:${filter.category ?? "all"}`;
    const inFlight = this.inFlightByUserId.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const request = this.staffFieldsForActiveUser()
      .then((staffFields) =>
        this.apiService.sendGetRequest<ItemsForUserResponse, ItemsForUserQuery>(`${apiPath.item}/user/${userId}`, {
          ...staffFields,
          ...(filter.statuses && { status: filter.statuses }),
          ...(filter.category && { category: filter.category }),
        }),
      )
      .then((response) => response.items.map(toItem))
      .finally(() => this.inFlightByUserId.delete(cacheKey));

    this.inFlightByUserId.set(cacheKey, request);
    return request;
  }

  public async getForOrder(order: { items?: string[]; userId: string }): Promise<Item[]> {
    if (!order.items?.length) {
      return [];
    }

    const response = await this.apiService.sendGetRequest<ItemsForUserResponse, ItemsForUserQuery>(
      `${apiPath.item}/user/${order.userId}`,
      { ...(await this.staffFieldsForActiveUser()), ids: order.items, sortBy: "requestedIds" },
    );
    return response.items.map(toItem);
  }

  public async create(item: Omit<Item, "numberId">): Promise<string> {
    const body: CreateItemRequest = {
      bay: item.bay,
      category: item.category as unknown as CreateItemRequest["category"],
      city: item.city as unknown as CreateItemRequest["city"],
      userId: item.userId,
      brand: item.brand,
      color: item.color,
      description: item.description,
      image: item.image,
      name: item.name,
      notes: notesToRequest(item.notes),
      number: item.number,
      row: item.row,
      seasons: (item.seasons ?? []) as unknown as CreateItemRequest["seasons"],
      status: item.status as unknown as CreateItemRequest["status"],
      warehouse: item.warehouse,
      height: item.dimension?.height,
      length: item.dimension?.length,
      width: item.dimension?.width,
      weight: item.dimension?.weight,
      dimensionUnit: item.dimension?.dimensionUnit as CreateItemRequest["dimensionUnit"],
      weightUnit: item.dimension?.weightUnit as CreateItemRequest["weightUnit"],
    };
    const response = await this.apiService.sendPostRequest<CreateItemRequest, ItemResponse>(apiPath.item, body);
    return response.itemId;
  }

  public async update(id: string, updates: Partial<Item>): Promise<void> {
    const body: UpdateItemRequest = {};
    if (updates.bay !== undefined) body.bay = updates.bay;
    if (updates.brand !== undefined) body.brand = updates.brand;
    if (updates.category !== undefined) body.category = updates.category as unknown as UpdateItemRequest["category"];
    if (updates.city !== undefined) body.city = updates.city as unknown as UpdateItemRequest["city"];
    if (updates.color !== undefined) body.color = updates.color;
    if (updates.description !== undefined) body.description = updates.description;
    if (updates.image !== undefined) body.image = updates.image;
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.notes !== undefined) body.notes = notesToRequest(updates.notes);
    if (updates.number !== undefined) body.number = updates.number;
    if (updates.row !== undefined) body.row = updates.row;
    if (updates.seasons !== undefined)
      body.seasons = (updates.seasons ?? []) as unknown as UpdateItemRequest["seasons"];
    if (updates.status !== undefined) body.status = updates.status as unknown as UpdateItemRequest["status"];
    if (updates.dimension) {
      body.height = updates.dimension.height;
      body.length = updates.dimension.length;
      body.width = updates.dimension.width;
      body.weight = updates.dimension.weight;
      body.dimensionUnit = updates.dimension.dimensionUnit as UpdateItemRequest["dimensionUnit"];
      body.weightUnit = updates.dimension.weightUnit as UpdateItemRequest["weightUnit"];
    }
    await this.apiService.sendPatchRequest<UpdateItemRequest, void>(`${apiPath.item}/${id}`, body);
  }

  public async softDelete(id: string): Promise<void> {
    await this.apiService.sendPatchRequest<Record<string, never>, void>(`${apiPath.item}/${id}/soft-delete`, {});
  }

  public async delete(id: string): Promise<void> {
    await this.apiService.sendDeleteRequest<void>(`${apiPath.item}/${id}`, undefined);
  }

  public async getPacklistsByUser(userId: string): Promise<PacklistResponse[]> {
    return this.apiService.sendGetRequest<PacklistResponse[]>(`${apiPath.packlist}/user/${userId}`, undefined);
  }

  public async getAll(category?: ItemCategory, cancelled?: AbortSignal): Promise<Item[]> {
    const staffFields = await this.staffFieldsForActiveUser();

    return fetchEveryPage(async (page) => {
      const response = await this.apiService.sendGetRequest<ItemsDataPage, ItemsQuery>(apiPath.item, {
        ...staffFields,
        ...page,
        ...(category && { category }),
      });
      return { items: response.items.map(toItem), totalCount: response.totalCount };
    }, cancelled);
  }

  public async getItemsWithSearch(search: string): Promise<Item[]> {
    const response = await this.apiService.sendGetRequest<ItemsDataPage, ItemsQuery>(apiPath.item, {
      ...(await this.staffFieldsForActiveUser()),
      search,
    });
    return response.items.map(toItem);
  }

  public async getByNumberId(numberId: number): Promise<Item | undefined> {
    const response = await this.apiService.sendGetRequest<ItemsDataPage, ItemsQuery>(apiPath.item, {
      ...(await this.staffFieldsForActiveUser()),
      search: String(numberId),
    });
    const match = response.items.find((i) => i.numberId === numberId);
    return match ? toItem(match) : undefined;
  }

  private async staffFieldsForActiveUser(): Promise<StaffFieldsQuery> {
    return (await this.authService.isActiveUserAdmin()) ? staffOnlyFields : {};
  }
}
